import { InsightType } from "@amzn/d16-g-client-deck-builder-typescript-client";
import type { TagVariant } from "@amzn/storm-ui";
import { Card, Flex, Heading, Text, Tag, Divider } from "@amzn/storm-ui";
import styled from "styled-components";

import { StyleConstants } from "../../../../config/constants";

export const StyledInsightCard = styled(Card)`
    height: 300px;

    > div:first-child {
        flex: 1;
    }
`;

export const StyledInsightCardBody = styled(Flex)`
    height: 100%;
`;

export const StyledDescriptorContainer = styled(Flex)`
    width: 100%;
    flex-direction: column;
    container-type: inline-size;
`;

export const StyledRowReverse = styled(Flex)`
    width: 100%;
    flex-direction: row-reverse;
`;

export const StyledDescriptorHeading = styled(Heading)`
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    margin-top: ${({ theme }) => theme.spacing.xxsmall.default};
    margin-bottom: ${({ theme }) => theme.spacing.small.default};
    word-wrap: break-word;
    max-width: 100%;
    font-family: "Amazon Ember", Helvetica, sans-serif !important;
`;

export const StyledDescriptorText = styled(Text)`
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
    margin-top: ${({ theme }) => theme.spacing.xxsmall.default};
    margin-bottom: ${({ theme }) => theme.spacing.small.default};
    word-wrap: break-word;
    max-width: 100%;
`;

export const StyledCardDetailsText = styled(Text)<{ lineClamp?: number }>`
    text-overflow: ellipsis;
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: ${(props) => props.lineClamp ?? 1};
    -webkit-box-orient: vertical;
`;

export interface DynamicLineClamp {
    width: number;
    lineClamp: number;
}
export const StyledCardDynamicClamp = styled(Text)`
    text-overflow: ellipsis;
    overflow: hidden;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    @media (min-width: 600px) {
        -webkit-line-clamp: 1;
    }

    @media (min-width: 700px) {
        -webkit-line-clamp: 2;
    }

    @media (min-width: 800px) {
        -webkit-line-clamp: 3;
    }

    @media (min-width: 900px) {
        -webkit-line-clamp: 4;
    }

    @media (min-width: 1100px) {
        -webkit-line-clamp: 5;
    }
`;

export const StyledAdditionalObjectInfomation = styled.div``;

export const StyledCardFooter = styled.div`
    width: 100%;
    margin-top: auto;
`;

export const StyledInsightSheetHeader = styled.div`
    padding: 1rem;
    top: 0px;
    z-index: 5;
    background-color: white;
    border-bottom: thin solid rgb(230, 233, 237);
`;

export const StyledSheetFooter = styled.footer`
    position: sticky;
    bottom: 0px;
    background-color: white;
    border-top: thin solid rgb(230, 233, 237);
    padding: 1rem;
    z-index: 100;
`;

export const StyledInsightSheetBody = styled.div`
    width: ${StyleConstants.InsightDetailWidth};
    display: flex;
    min-height: 100vh;
    justify-content: space-between;
    flex-direction: column;
`;

export const StyledDetailSheetPDFViewerContainer = styled.div`
    padding: 1rem;
    display: flex;
    flex-flow: column;
`;

export const StyledDetailSheetTitleContainer = styled.div`
    margin-top: 0.75rem;
`;

export const StyledDetailSheetDetailsContainer = styled.div`
    margin-top: 1rem;
`;

export const SpaceBetweenFlexBox = styled.div`
    display: flex;
    justify-content: space-between;
    width: 100%;
    align-items: first baseline;
`;

export const CardRecommendationContainer = styled.div`
    margin-top: 1px;
    padding ${({ theme }) => theme.spacing.small.default};
    width: 100%;
    text-align: left;
    box-sizing: border-box;
    background: ${({ theme }) => theme.color.purple[100]};
    border-radius: 0 0 10px 10px;
`;

export const CardRecommendationContent = styled(Text)`
    padding: ${({ theme }) => theme.spacing.xsmall.default} 0 0 0;
    height: 12vh;
    overflow: auto;
`;

export const StyledTag = styled(Tag)`
    font-size: 12px !important;
    border-radius: 8px !important;
    margin-top: ${({ theme }) => theme.spacing.xxsmall.default} !important;
    margin-bottom: ${({ theme }) => theme.spacing.xxsmall.default} !important;
`;

const baseTextStyles = `
    display: -webkit-box;
    -webkit-box-orient: vertical;
    overflow: hidden;
    word-wrap: break-word;
    max-width: 100%;
    -webkit-line-clamp: 3;
`;

export const StyledCardHeader = styled(Heading).attrs({ renderAs: "h6" })`
    ${baseTextStyles};
    margin: 0;
    && {
        font-family: "Amazon Ember", Helvetica, sans-serif;
    }
`;

export const StyledDescriptorPrimaryContextText = styled(Text)`
    ${baseTextStyles};
    font-weight: bold;
    font-size: small;
    && {
        margin: 0 0 ${({ theme }) => theme.spacing.xxxxsmall.default} 0;
    }
`;

export const StyledDescriptorSecondaryContextText = styled(Text)`
    ${baseTextStyles};
    font-size: small;
    && {
        margin: 0 0 ${({ theme }) => theme.spacing.xxxsmall.default} 0;
    }
`;

export const StyledAdditionalInfoText = styled(Text)`
    ${baseTextStyles};
    && {
        margin: ${({ theme }) => theme.spacing.xxxxsmall.default} 0 0 0;
    }
`;

export const StyledFooterText = styled(Text)`
    ${baseTextStyles};
    margin: 0;
    font-size: 12px;
    && {
        color: ${({ theme }) => theme.color.tertiary};
    }
`;

export const StyledDivider = styled(Divider)`
    && {
        margin: ${({ theme }) => theme.spacing.xxsmall.default} 0;
    }
`;

export const StyledSummaryHeader = styled.div`
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing.xxxsmall.default};
    margin-bottom: ${({ theme }) => theme.spacing.xsmall.default};
`;

export const insightTypeToTagVariant: Partial<Record<InsightType, TagVariant>> =
    {
        [InsightType.INSIGHT]: "violet",
        [InsightType.ISTORE]: "violet",
        [InsightType.PACKAGING]: "info",
        [InsightType.CDB_TEMPLATE]: "info",
        [InsightType.MARKETING]: "redOrange",
        [InsightType.PITCHED_CONTENT]: "chartreuse",
    };
